import { DEALER_GROUP_CATEGORY } from "../../common/Constants";

function selectCanadaDealer(data, user) {
  if (data.length <= 1) {
    return data[0];
  }

  const filtered = data.filter((dealer) =>
    dealer.commonId?.endsWith(user.brand)
  );

  const dealer = filtered.find(
    (currentDealer) =>
      currentDealer.dealerType === "MDLR" || currentDealer.dealerType === "HUB"
  );

  return dealer || filtered[0];
}

function selectUsaDealer(data, salesCode) {
  const hasCategory = data.some(
    (dealer) => dealer.dealerLocationCategory != null
  );

  if (!hasCategory) {
    return salesCode
      ? data.find((dealer) => dealer.salesCode === salesCode)
      : data[0];
  }

  const bySalesCode = salesCode
    ? data.filter((dealer) => dealer.salesCode === salesCode)
    : data;

  return (
    bySalesCode.find(
      (dealer) =>
        dealer.dealerLocationCategory === DEALER_GROUP_CATEGORY.PRIMARY
    ) ||
    bySalesCode.find(
      (dealer) =>
        dealer.dealerLocationCategory === DEALER_GROUP_CATEGORY.SECONDARY
    ) ||
    bySalesCode.find(
      (dealer) => dealer.dealerLocationCategory === DEALER_GROUP_CATEGORY.OTHER
    ) ||
    bySalesCode[0]
  );
}

/**
 * Selects the appropriate dealer from dealer group API data based on market and
 * optional dealerLocationCategory (USA only). Used by NADealerApp and EcommDealerOnboardingApp.
 *
 * @param {Array} data - Dealer group array from DealerGroupingClient.getDealerGroup
 * @param {Object} user - NADealer (or compatible) instance with isCanadaDealer(), isUsaDealer(), brand
 * @param {string|null} salesCode - Current sales code, e.g. from sessionStorage
 * @returns {Object|undefined} Selected dealer object, or undefined if none found
 */
export function selectDealerFromGroup(data, user, salesCode) {
  if (!Array.isArray(data) || data.length === 0) {
    return undefined;
  }

  if (user?.isCanadaDealer?.()) {
    return selectCanadaDealer(data, user);
  }

  if (user?.isUsaDealer?.()) {
    return selectUsaDealer(data, salesCode);
  }

  return undefined;
}

/**
 * Picks the dealer row that matches the active session when dealer group has multiple entries
 * e.g. Ford + Lincoln same PA. Prefer commonId equality, then PA + sales code, then PA.
 *
 * @param {Array|undefined} dealerGroup - Rows from dealer group API
 * @param {string|undefined} dealerCommonId - Current context common id
 * @param {{ dealerId?: string, salesCode?: string }} [user] - Context for fallbacks
 * @returns {Object|undefined}
 */
export function findDealerInGroupByContext(dealerGroup, dealerCommonId, user) {
  if (!dealerGroup?.length || !dealerCommonId) {
    return undefined;
  }

  const byCommonId = dealerGroup.find(
    (dealer) => dealer.commonId === dealerCommonId
  );

  if (byCommonId) {
    return byCommonId;
  }

  const pa = user?.dealerId;
  const salesCode = user?.salesCode;

  if (pa && salesCode) {
    const byPaAndSales = dealerGroup.find(
      (dealer) => dealer.paCode === pa && dealer.salesCode === salesCode
    );

    if (byPaAndSales) {
      return byPaAndSales;
    }
  }

  if (pa) {
    return dealerGroup.find((dealer) => dealer.paCode === pa);
  }

  return undefined;
}

/**
 * Maps dealer group API address onto DealerInfoDto address field names for onboarding progress.
 *
 * @param {Object|undefined} dealer - Single dealer row from dealer group API
 * @returns {Record<string, string>}
 */
export function mapDealerGroupRowToAddressPayload(dealer) {
  const addr = dealer?.address;

  if (!addr) {
    return {};
  }

  const out = {};

  if (addr.street) {
    out.addressStreet1 = addr.street;
  }

  if (addr.city) {
    out.addressCity = addr.city;
  }

  if (addr.state) {
    out.addressState = addr.state;
  }

  if (addr.postalCode) {
    out.addressZip = addr.postalCode;
  }

  return out;
}
